import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { get, invert } from 'lodash';
import qs from 'qs';

import { Filter, Main } from 'Containers';
import { createToast, toastType } from 'Common/ToastHelper';
import { getIsDirty } from 'Common/Selectors/DirtySelector';
import { ReportGeneration } from 'Common/ObjectHelper';
import MPWEditor from '../components/MPWEditor';
import SelectSitesTable from 'Common/SelectSitesTable';
import { LanguageEnum } from 'Common/Constants/Enumeration/LanguageEnum';
import { TitledCard } from 'Components';
import { DirtyWrapper } from 'Components';
import { forceDirtyState } from 'State/dirtyTracking/actions';
import { resetDirty } from 'State/dirtyTracking/actions';

import {
  writeCustomLetter,
  getPreview,
  clearPreview,
} from '../state/setup/actions';
import { getSiteSetup } from '../../sitesetup/state/page/actions';
import * as LetterSetupSelectors from '../state/selectors';

const LanguageMap = invert(LanguageEnum);

class LetterEditContainer extends Component {
  static path = '/lettersetup/edit/:templateId';

  constructor(props) {
    super(props);
    this.editorRef = React.createRef();
    const query = qs.parse(props.location.search, {
      ignoreQueryPrefix: true,
    });
    const templateId = parseInt(props.match.params.templateId, 10);
    const letterCustomId = parseInt(query.customId, 10);
    const languageId = parseInt(query.languageId, 10);
    const template = props.getTemplate(templateId);
    const letterCustom = letterCustomId
      ? props.getLetterCustom(templateId, letterCustomId)
      : null;

    let initialValue = get(template, 'Body');
    const initialSelectedSites = [];
    /**
     * Get Body from the custom letter or the default template
     * Get Site that the custom letter was written to
     */
    if (letterCustom) {
      initialValue = get(letterCustom, 'Body');
      const SiteId = letterCustom.SiteId;
      initialSelectedSites.push(SiteId);
    }
    this.state = {
      template,
      letterCustom,
      initialSelectedSites,
      selectedSites: initialSelectedSites,
      initialValue,
      languageId,
      selectedSitesIsDirty: false,
    };
  }

  getRootUrl = () => this.props.match.url.replace(/\/edit\/.*/gi, '');

  componentDidMount() {
    if (!this.state.template) {
      return this.props.history.replace(this.getRootUrl());
    }
    this.props.getSiteSetup();
  }

  componentDidUpdate(prevProps, prevState) {
    const { preview } = this.props;
    if (prevProps.preview !== preview && preview) {
      if (preview === 'No Data') {
        createToast(toastType.info, 'Results', 'No data found!', true);
      } else {
        ReportGeneration(preview, 'Letter Edit Preview');
      }
      this.props.clearPreview();
    }
  }

  getMainProps = () => {
    const { isReadOnly, isFetching, previewLoading } = this.props;
    return {
      shouldNotUsePageWidth: true,
      toolbox: {
        backButtonProps: { shown: true, path: this.getRootUrl() },
        rightButtons: [
          // {
          //   content: 'Log HTML',
          //   disabled: isFetching || isReadOnly,
          //   onClick: this.logHTML,
          // },
          {
            loading: previewLoading,
            content: 'Preview',
            disabled: previewLoading || isReadOnly,
            onClick: this.preview,
          },
          {
            loading: isFetching,
            content: 'Save',
            disabled: isFetching || isReadOnly,
            onClick: this.save,
          },
        ],
        shown: true,
      },
    };
  };

  save = () => {
    const { template, selectedSites } = this.state;
    const { writeCustomLetter, resetDirty } = this.props;
    if (!template) {
      return createToast(
        toastType.error,
        'Error',
        'Please return to the Letter Setup page and select a template',
        true
      );
    }
    const { IsSiteSpecific, Id } = template;

    const Sites = selectedSites;
    /**
     * If site specific, user must select site(s)
     */
    if (IsSiteSpecific && Sites.length < 1) {
      return createToast(
        toastType.error,
        'Error',
        'Please select at least one site or site group',
        true
      );
    }
    const Body = this.editorRef.current.getCurrentHTML();
    const margin = this.editorRef.current.state.margins.top;
    const pageSize = this.editorRef.current.state.pageSize;
    const isLegal = pageSize === 'legal';
    writeCustomLetter(Id, Body, Sites, margin, isLegal);
    resetDirty();
  };

  preview = () => {
    const html = this.editorRef.current.getCurrentHTML();
    const margin = this.editorRef.current.state.margins.top;
    const pageSize = this.editorRef.current.state.pageSize;
    const isLegal = pageSize === 'legal';
    this.props.getPreview(html, margin, isLegal);
  };

  logHTML = () => {
    const html = this.editorRef.current.getCurrentHTML();
    console.log(html);
  };

  onSelectedSiteChange = selectedSites => {
    const { selectedSitesIsDirty } = this.state;
    const { forceDirtyState, resetDirty } = this.props;

    this.setState({ selectedSites });
    if (selectedSites.length > 0 && !selectedSitesIsDirty) {
      forceDirtyState();
      this.setState({ selectedSitesIsDirty: true });
    }

    if (selectedSites.length === 0 && selectedSitesIsDirty) {
      resetDirty();
      this.setState({ selectedSitesIsDirty: false });
    }
  };

  renderSitesSelect() {
    return (
      <div style={{ width: 816 }}>
        <TitledCard title="Assign To">
          <SelectSitesTable
            initialSelectedSiteIds={this.state.initialSelectedSites}
            onChange={this.onSelectedSiteChange}
          />
        </TitledCard>
      </div>
    );
  }

  getDescriptions() {
    const { sites } = this.props;
    const { template, letterCustom, languageId } = this.state;
    const descriptions = [];

    let templateDescription = get(template, 'Description', '');
    if (letterCustom) {
      const SiteId = letterCustom.SiteId;
      const site = sites.find(s => s.Id === SiteId);
      if (site) {
        descriptions.push(site.Description);
      }
    }
    if (templateDescription) {
      descriptions.push(templateDescription);
    }
    if (languageId && LanguageMap[languageId]) {
      descriptions.push(LanguageMap[languageId]);
    }
    return descriptions.join(' / ');
  }

  render() {
    const { initialValue, template, letterCustom } = this.state;
    const { forceDirtyState, resetDirty } = this.props;

    const tokens = get(template, 'Tokens');
    const IsSiteSpecific = get(template, 'IsSiteSpecific');
    const customMargin = get(letterCustom, 'Margin', 1);
    const isLegal = get(letterCustom, 'IsLegal', false);
    const customPageSize = isLegal ? 'legal' : 'letter';

    const margin = {
      top: customMargin,
      bottom: customMargin,
      left: customMargin,
      right: customMargin,
    };

    return (
      <Fragment>
        <Filter title="Letter Edit" />
        <Main {...this.getMainProps()}>
          <DirtyWrapper closeModalYesCallback={this.onCancelClick}>
            {showDirtyConfirm => (
              <MPWEditor
                onEditChange={showDirtyConfirm}
                ref={this.editorRef}
                name={this.getDescriptions()}
                tokens={tokens}
                initialValue={initialValue}
                initialPageSize={customPageSize}
                initialMargins={margin}
                focusOnMount={false}
                forceDirtyState={forceDirtyState}
                resetDirty={resetDirty}
              />
            )}
          </DirtyWrapper>
          {IsSiteSpecific && this.renderSitesSelect()}
        </Main>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  ownProps.setPageLevelDirty(getIsDirty()(state));

  return {
    siteGroups: get(state, 'SiteSetupState.page.siteGroups', []),
    sites: get(state, 'SiteSetupState.page.siteSetup.Data', []),
    getTemplate: letterId => LetterSetupSelectors.getTemplate(state, letterId),
    getLetterCustom: (letterId, letterCustomId) =>
      LetterSetupSelectors.getLetterCustom(state, letterId, letterCustomId),
    preview: LetterSetupSelectors.getPreview(state),
    previewLoading: LetterSetupSelectors.getPreviewLoading(state),
    isFetching: LetterSetupSelectors.getFetching(state),
  };
};

const mapDispatchToProps = {
  writeCustomLetter,
  getSiteSetup,
  getPreview,
  clearPreview,
  forceDirtyState,
  resetDirty,
};

export const LetterEdit = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(LetterEditContainer)
);



// WEBPACK FOOTER //
// ./src/pages/lettersetup/subpages/Edit.js